/**
 * Analytics for pages that run inside a customer's <iframe> (school, municipality
 * and association websites such as visenti.ch).
 *
 * Visitors in an iframe are real visitors of our content and must show up in GA4
 * like any other visitor:
 *
 * - Cookies: inside the customer's page our `_ga` cookie is a third-party cookie.
 *   Without `SameSite=None; Secure` Chrome drops it, so every page view looks like
 *   a new visitor. `Partitioned` lets browsers that partition third-party storage
 *   (Chrome without third-party cookies, Firefox) keep it per customer site.
 *   Safari still blocks it; GA4 then counts each iframe load as a new visitor.
 * - Attribution: every event from an iframe carries `partner_website` (the
 *   customer's website). GA4 attributes the session to that site as referral on
 *   its own; the parameter also tags events that happen later in the session.
 *
 * Event and parameter names live in `ga4-events.ts`.
 */

/** Cookie flags for GA4 when the app runs inside a third-party iframe. */
export const EMBED_COOKIE_FLAGS = 'SameSite=None;Secure;Partitioned';

/** Value of `partner_website` when the browser hides the embedding site. */
export const UNKNOWN_PARTNER = '(unbekannt)';

/** Whether this window is inside an iframe (cross-origin access counts as yes). */
export function isInIframe(win: Window = window): boolean {
  try {
    return win.self !== win.top;
  } catch {
    return true;
  }
}

/**
 * Host name of the website that embeds us, e.g. `visenti.ch`. `null` outside an
 * iframe or when the browser does not reveal the parent.
 */
export function embedHost(
  win: Window = window,
  doc: Document = document
): string | null {
  if (!isInIframe(win)) {
    return null;
  }
  // ancestorOrigins (Chrome, Safari) survives strict referrer policies; Firefox
  // only has the referrer, which the first iframe load sets to the parent page.
  const ancestors = win.location.ancestorOrigins;
  const candidates = [ancestors?.[0], doc.referrer];
  for (const candidate of candidates) {
    if (!candidate) continue;
    try {
      const host = new URL(candidate).hostname.replace(/^www\./, '');
      if (host && host !== win.location.hostname) {
        return host;
      }
    } catch {
      // not a URL, try the next one
    }
  }
  return null;
}
